import React from 'react';
import { Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import { MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * A single prominent row action, surfaced inline instead of being buried in the
 * ⋯ overflow menu. `danger` actions (e.g. Delete) render icon-only in a trailing
 * slot after the ⋯ button; everything else renders as a labelled button before it.
 */
export interface RowPrimaryAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Trailing, icon-only, destructive styling (Delete). */
  danger?: boolean;
  /** Coral-filled primary CTA (the single most relevant action for the row). */
  tone?: 'primary' | 'default';
}

const BASE_BTN: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  height: 26,
  padding: '0 8px',
  borderRadius: 6,
  fontSize: 11.5,
  fontWeight: 600,
  lineHeight: 1,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  border: '1px solid transparent',
  transition: 'background .12s, border-color .12s, color .12s',
};

function ActionButton({
  action,
  iconOnly,
  instantTooltip,
}: {
  action: RowPrimaryAction;
  iconOnly?: boolean;
  instantTooltip?: boolean;
}) {
  const { tone, danger, disabled } = action;
  const style: React.CSSProperties = { ...BASE_BTN };

  if (iconOnly) {
    style.padding = 0;
    style.width = 26;
    style.justifyContent = 'center';
  }

  if (danger) {
    style.color = 'var(--color-destructive)';
    style.borderColor = 'var(--color-warm-border)';
    style.background = 'var(--color-paper)';
  } else if (tone === 'primary') {
    style.color = '#fff';
    style.background = 'var(--color-coral)';
    style.borderColor = 'var(--color-coral)';
  } else {
    style.color = 'var(--color-ink-2)';
    style.borderColor = 'var(--color-warm-border)';
    style.background = 'var(--color-paper)';
  }

  if (disabled) {
    style.opacity = 0.5;
    style.cursor = 'not-allowed';
  }

  const btn = (
    <button
      type="button"
      disabled={disabled}
      aria-label={action.label}
      title={instantTooltip ? undefined : action.label}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) action.onClick();
      }}
      style={style}
    >
      {action.icon}
      {!iconOnly && <span>{action.label}</span>}
    </button>
  );

  // Instant tooltip (no hover delay) — used for the icon-only reveal options so
  // pointing at an icon shows its name immediately.
  if (instantTooltip) {
    return (
      <Tooltip title={action.label} mouseEnterDelay={0} mouseLeaveDelay={0} placement="top">
        {btn}
      </Tooltip>
    );
  }
  return btn;
}

export interface RowActionButtonsProps {
  /** Inline actions for this row (already filtered/gated by status upstream). */
  actions: RowPrimaryAction[];
  /** Remaining overflow actions rendered under the ⋯ button. */
  menuItems: MenuProps['items'];
  className?: string;
  /** When true, the action cluster is hidden and slides in from the right on
   *  row hover/focus. The row must carry the Tailwind `group` class. */
  revealOnHover?: boolean;
  /** When true, the reveal menu opens just to the RIGHT of where the cursor
   *  first entered the row (its left edge tracks the cursor's x) instead of
   *  always at the row's right edge. The row must be a positioned ancestor
   *  (position: relative) and set `--cp-reveal-left` to the px distance from
   *  the row's left edge to the cursor on mouse-enter. Opt-in per surface. */
  anchorToCursor?: boolean;
}

/**
 * Renders a row's prominent inline actions followed by an ⋯ overflow menu:
 *   [ primary… ] [ secondary… ]  [⋯]  [🗑 danger]
 * Reused by the Content Planner table and list rows so both surface the same
 * actions instead of hiding them all in the kebab.
 */
/** Flatten an antd overflow menu into inline action descriptors (skips dividers)
 *  so the hidden ⋯ options can be rendered as buttons on hover. */
function menuItemsToActions(items: MenuProps['items']): RowPrimaryAction[] {
  if (!items) return [];
  const out: RowPrimaryAction[] = [];
  for (const it of items) {
    if (!it || (it as { type?: string }).type === 'divider') continue;
    const a = it as {
      key?: React.Key;
      label?: React.ReactNode;
      icon?: React.ReactNode;
      onClick?: () => void;
      disabled?: boolean;
      danger?: boolean;
    };
    out.push({
      key: String(a.key ?? out.length),
      label: typeof a.label === 'string' ? a.label : String(a.label ?? ''),
      icon: a.icon,
      onClick: a.onClick ?? (() => {}),
      disabled: a.disabled,
      danger: a.danger,
    });
  }
  return out;
}

// Cross-row pin coordination (module singleton): at most ONE row's reveal
// menu may be pinned at a time, and while one is pinned, hover-reveals on
// every OTHER row are suppressed — two open menus at once read as clutter and
// the pinned one is clearly the user's focus. Pinning a second row moves the
// pin (the first closes). useSyncExternalStore keeps every instance in sync
// without needing a shared provider above the table.
let pinnedRowId: string | null = null;
const pinnedListeners = new Set<() => void>();
function setPinnedRowId(id: string | null) {
  pinnedRowId = id;
  pinnedListeners.forEach((l) => l());
}
function subscribePinned(cb: () => void) {
  pinnedListeners.add(cb);
  return () => {
    pinnedListeners.delete(cb);
  };
}
function usePinnedRowId(): string | null {
  return React.useSyncExternalStore(subscribePinned, () => pinnedRowId);
}

/** One line of the vertical reveal menu: icon + visible label, full width. */
function MenuItemButton({ action, revealIndex }: { action: RowPrimaryAction; revealIndex: number }) {
  const { danger, disabled } = action;
  return (
    <button
      type="button"
      className="ctt-reveal-item"
      aria-label={action.label}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) action.onClick();
      }}
      style={{
        ['--reveal-i' as string]: revealIndex,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        padding: '6px 10px',
        borderRadius: 6,
        border: 'none',
        background: 'transparent',
        textAlign: 'left',
        fontSize: 12.5,
        fontWeight: 500,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        color: danger ? 'var(--color-destructive)' : 'var(--color-ink-1)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={(e) => {
        if (!disabled) e.currentTarget.style.background = 'var(--color-warm-bg)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <span style={{ display: 'inline-flex', flexShrink: 0, color: danger ? 'var(--color-destructive)' : 'var(--color-ink-3)' }}>
        {action.icon}
      </span>
      {action.label}
    </button>
  );
}

export function RowActionButtons({ actions, menuItems, className, revealOnHover, anchorToCursor }: RowActionButtonsProps) {
  const lead = actions.filter((a) => !a.danger);
  const trail = actions.filter((a) => a.danger);
  const hasMenu = Array.isArray(menuItems) && menuItems.length > 0;
  const revealId = React.useId();
  const pinnedId = usePinnedRowId();
  const pinned = pinnedId === revealId;
  const otherPinned = pinnedId !== null && !pinned;
  // A pinned row that unmounts (filter/pagination) must release the pin, or
  // every other row stays hover-suppressed forever.
  React.useEffect(
    () => () => {
      if (pinnedRowId === revealId) setPinnedRowId(null);
    },
    [revealId],
  );

  const menu = hasMenu ? (
    <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
      <button
        type="button"
        aria-label="More actions"
        title="More actions"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'grid',
          placeItems: 'center',
          width: 26,
          height: 26,
          borderRadius: 6,
          background: 'transparent',
          border: 'none',
          color: 'var(--color-ink-3)',
          cursor: 'pointer',
        }}
      >
        <MoreHorizontal size={16} />
      </button>
    </Dropdown>
  ) : null;

  // Always-visible variant: quick buttons, then the persistent kebab, then delete.
  if (!revealOnHover) {
    return (
      <div
        className={className}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}
        onClick={(e) => e.stopPropagation()}
      >
        {lead.map((a) => (
          <ActionButton key={a.key} action={a} />
        ))}
        {menu}
        {trail.map((a) => (
          <ActionButton key={a.key} action={a} iconOnly />
        ))}
      </div>
    );
  }

  // Hover-reveal: the kebab stays visible at rest so the row always shows it's
  // actionable. On hover (or pin via the kebab) the FULL action set — the
  // primaries AND every option otherwise hidden in the ⋯ menu — is revealed as
  // a floating VERTICAL menu: one item per line, icon + visible label,
  // danger options separated at the bottom. Chosen over the horizontal strip
  // (which reached across Created/Words and covered the Schedule column,
  // flagged 2026-07-27) and over icon-only buttons: a vertical list has the
  // narrowest horizontal footprint and needs no tooltips to be readable —
  // it's the menu pattern users already know, opening like a dropdown below
  // the row and overlapping rows beneath it, which is expected menu behavior.
  // Absolutely positioned so it never widens the cell at rest.
  const menuActions = menuItemsToActions(menuItems);
  return (
    <div
      style={{
        // When anchoring to the cursor this wrapper must NOT be the positioning
        // context, so the absolute menu below resolves to the row (position:
        // relative) and `--cp-reveal-left` is measured in row coordinates.
        position: anchorToCursor ? 'static' : 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        minHeight: 26,
        minWidth: 26,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* ⋯ is now a toggle: it pins the reveal drawer open (stays in place)
          instead of opening the old dropdown menu. */}
      <button
        type="button"
        aria-label={pinned ? 'Hide actions' : 'Show actions'}
        aria-pressed={pinned}
        title={pinned ? 'Hide actions' : 'Show actions'}
        onClick={(e) => {
          e.stopPropagation();
          setPinnedRowId(pinned ? null : revealId);
        }}
        style={{
          position: 'relative',
          zIndex: 6,
          display: 'grid',
          placeItems: 'center',
          width: 26,
          height: 26,
          borderRadius: 6,
          border: 'none',
          cursor: 'pointer',
          background: pinned ? 'var(--color-coral-light)' : 'transparent',
          color: pinned ? 'var(--color-coral)' : 'var(--color-ink-3)',
        }}
      >
        <MoreHorizontal size={16} />
      </button>
      <div
        className={cn(
          className,
          // Pinned via ⋯ is a click — an explicit request — so it opens with no
          // transition at all. Hover keeps the 200ms ease; delay-0 is its resting
          // delay, so only the group-hover state below carries the dwell.
          pinned
            ? 'transition-none'
            : 'transition-all duration-200 ease-out delay-0',
          pinned && 'ctt-reveal-open',
          pinned
            ? // Pinned via the ⋯ toggle: show at once — no dwell, no fade/slide.
              'visible translate-x-0 opacity-100 pointer-events-auto'
            : otherPinned
              ? // Another row is pinned: this row's hover must reveal nothing.
                'invisible translate-x-full opacity-0 pointer-events-none'
              : 'invisible translate-x-full opacity-0 pointer-events-none ' +
                // Dwell 3s on the row before the menu appears, so sweeping the
                // pointer across the list doesn't flash a menu on every row. `invisible` (not just opacity/pointer-events) is
                // what makes the delay safe: visibility honours transition-delay,
                // so nothing is hit-testable during the wait. Leaving reverts to
                // delay-0 and hides instantly.
                // NOTE: written as a literal — Tailwind scans source text, so a
                // class assembled from a variable would never be generated.
                'group-hover:delay-[3000ms] group-hover:visible group-hover:translate-x-0 ' +
                'group-hover:opacity-100 group-hover:pointer-events-auto',
        )}
        style={{
          position: 'absolute',
          // Cursor-anchored: the menu's LEFT edge sits at the pointer's x so the
          // options open to the RIGHT of the cursor. Clamped so the ~176px-wide
          // menu never overflows the row's right edge (leaves room for the ⋯)
          // nor starts before the row's left padding. Non-anchored rows keep the
          // fixed right-edge placement.
          left: anchorToCursor
            ? 'clamp(8px, var(--cp-reveal-left, 8px), calc(100% - 212px))'
            : undefined,
          right: anchorToCursor ? undefined : 32,
          top: '50%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 2,
          whiteSpace: 'nowrap',
          minWidth: 176,
          padding: 4,
          borderRadius: 8,
          background: 'var(--color-paper)',
          border: '1px solid var(--color-warm-border)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
          zIndex: 20,
        }}
      >
        {[...lead, ...menuActions].map((a, i) => (
          <MenuItemButton key={a.key} action={a} revealIndex={i} />
        ))}
        {trail.length > 0 && (
          <div
            role="separator"
            className="ctt-reveal-item"
            style={{
              ['--reveal-i' as string]: lead.length + menuActions.length,
              height: 1,
              margin: '2px 4px',
              background: 'var(--color-warm-border)',
            }}
          />
        )}
        {trail.map((a, i) => (
          <MenuItemButton
            key={a.key}
            action={a}
            revealIndex={lead.length + menuActions.length + 1 + i}
          />
        ))}
      </div>
    </div>
  );
}


export default RowActionButtons;
